import { type Store, createLens } from '@frontend/viewer-core';

import {
  type DxfSessionState,
  type EntityHighlights,
  type HighlightManager,
  type HighlightSpan,
  type Highlights,
  NO_HIGHLIGHTS,
} from './types';

export const createHighlightsSlice = (
  state: Store<DxfSessionState>,
): Store<Highlights> =>
  createLens(
    state,
    (current) => current.highlights,
    (current, highlights) => ({ ...current, highlights }),
  );

const hasSameEntities = (a: EntityHighlights, b: EntityHighlights) =>
  a.size === b.size && [...a].every(([key, color]) => b.get(key) === color);

const isSameSpan = (a: HighlightSpan, b: HighlightSpan) =>
  a.color === b.color &&
  a.segment.every(
    ([x, y], index) => x === b.segment[index][0] && y === b.segment[index][1],
  );

const hasSameSpans = (
  a: readonly HighlightSpan[],
  b: readonly HighlightSpan[],
) =>
  a.length === b.length && a.every((span, index) => isSameSpan(span, b[index]));

const hasSameHighlights = (a: Highlights, b: Highlights) =>
  hasSameEntities(a.entities, b.entities) && hasSameSpans(a.spans, b.spans);

const isEmpty = (highlights: Highlights) =>
  highlights.entities.size === 0 && highlights.spans.length === 0;

type Dependencies = {
  highlights: Store<Highlights>;
};

export const createHighlightController = ({
  highlights,
}: Dependencies): HighlightManager => ({
  getState: highlights.getState,
  subscribe: highlights.subscribe,

  set(next) {
    // Equal contents keep the old reference, so subscribers see no change.
    highlights.setState((previous) =>
      hasSameHighlights(previous, next)
        ? previous
        : { entities: new Map(next.entities), spans: [...next.spans] },
    );
  },

  clear() {
    highlights.setState((previous) =>
      isEmpty(previous) ? previous : NO_HIGHLIGHTS,
    );
  },
});
